/**
 * Driving distance and time between two points.
 *
 * The platform's own route service (OSRM, through the Backend) answers first. Google is the fallback, and only its
 * calls go to the Google API usage log.
 *
 * Google projects created from March 2025 on can no longer enable the legacy Distance Matrix, so the Google route is
 * computed with the Routes API (RouteMatrix.computeRouteMatrix) first. Older projects that only have the legacy
 * API enabled keep working through the legacy DistanceMatrixService fallback.
 *
 * The @types/googlemaps version pinned in the project predates the Routes library, hence the `any` casts below.
 */

import { saveGoogleApiUsageLog } from "@hooks/useGoogleApiUsageLog";
import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import { readPlatformDrivingRoute } from "@services/routing/platformRouting";

/** Driving route between two points */
export interface DrivingRoute {
  distanceMeters: number;
  durationSeconds: number;
}

/** User fields sent with the Google API usage log */
interface RouteLookupUser {
  name?: string;
  email?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the pinned typings lack importLibrary and the Routes library
const mapsApi = (): any => (window as any).google?.maps;

// Once the Routes API failed and the legacy one answered, keep using the legacy one for the rest of the page session
let preferLegacy = false;

/** Reset the API preference (tests only) */
export const resetRouteMatrixPreference = (): void => { preferLegacy = false; };

const computeWithRoutesApi = async (origin: google.maps.LatLngLiteral, destination: google.maps.LatLngLiteral): Promise<DrivingRoute | null> => {

  const maps = mapsApi();
  const routes = maps?.routes?.RouteMatrix ? maps.routes : await maps?.importLibrary?.("routes");

  if (!routes?.RouteMatrix) throw new Error("Routes API is not available in this Maps version");

  const { matrix } = await routes.RouteMatrix.computeRouteMatrix({
    origins: [origin],
    destinations: [destination],
    travelMode: "DRIVING",
    fields: ["distanceMeters", "durationMillis"]
  });

  const item = matrix?.rows?.[0]?.items?.[0];

  // A response without the item is not a "no route" answer: let the legacy API try
  if (!item) throw new Error("Unexpected Routes API response");

  // No route between the points: the item comes without distance and duration
  if (typeof item.distanceMeters !== "number" || typeof item.durationMillis !== "number") return null;

  return { distanceMeters: item.distanceMeters, durationSeconds: Math.round(item.durationMillis / 1000) };
};

const computeWithLegacyApi = (origin: google.maps.LatLngLiteral, destination: google.maps.LatLngLiteral): Promise<DrivingRoute | null> => {

  const maps = mapsApi();

  if (!maps?.DistanceMatrixService) return Promise.reject(new Error("Legacy Distance Matrix is not loaded"));

  return new Promise((resolve, reject) => {
    new maps.DistanceMatrixService().getDistanceMatrix(
      { origins: [origin], destinations: [destination], travelMode: maps.TravelMode.DRIVING },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DistanceMatrixResponse / DistanceMatrixStatus
      (response: any, status: string) => {

        if (status !== maps.DistanceMatrixStatus.OK) {
          reject(new Error(`Legacy Distance Matrix: ${status}`));

          return;
        }

        const element = response?.rows?.[0]?.elements?.[0];

        if (element?.status !== maps.DistanceMatrixElementStatus.OK || !element.distance || !element.duration) {
          resolve(null);

          return;
        }

        resolve({ distanceMeters: element.distance.value, durationSeconds: element.duration.value });
      }
    );
  });
};

/**
 * Driving distance and time from origin to destination: the platform's own route service first, Google only when it
 * has no answer
 * @param origin Start point
 * @param destination End point
 * @param user Logged user, for the Google API usage log
 * @returns The route, or null when no service finds a route between the points
 */
export const computeDrivingRoute = async (
  origin: google.maps.LatLngLiteral,
  destination: google.maps.LatLngLiteral,
  user?: RouteLookupUser
): Promise<DrivingRoute | null> => {

  const platformRoute = await readPlatformDrivingRoute(origin, destination);

  if (platformRoute) return platformRoute;

  saveGoogleApiUsageLog(GoogleApiRequestTypes.ARRIVAL_FORECAST, user);

  if (!preferLegacy) {
    try {
      return await computeWithRoutesApi(origin, destination);
    } catch (error) {
      console.log("Routes API unavailable, trying the legacy Distance Matrix", error);
    }
  }

  const route = await computeWithLegacyApi(origin, destination);

  preferLegacy = true;

  return route;
};
